import { firstBy } from 'thenby'
import { Injectable } from '@angular/core'
import { APIService } from 'api/module'
import { TranslateService } from '@ngx-translate/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { IdentityService } from 'main/services/identity.service'
import { PickerService } from 'common/util.pickers/module'
import { NotifyService } from 'common/util/services/notify.service'

import { SharingCreateLinkModalComponent } from '../components/sharingCreateLinkModal.component'

import {
  MediaFileBundle, MediaFile, MediaRootPermission,
  OneTimeAccessToken,
  MediaRootDetail,
  ElementsUser,
} from 'elements-sdk'

@Injectable({ providedIn: 'root' })
export class SharingService {
  constructor (
    private api: APIService,
    private ngbModal: NgbModal,
    private identity: IdentityService,
    private translate: TranslateService,
    private picker: PickerService,
    private notify: NotifyService,
  ) { }

  async show (
    bundles: MediaFileBundle[],
    directories: MediaFile[],
    permission: MediaRootPermission,
    root: MediaRootDetail,
  ): Promise<void> {
    if (bundles.length + directories.length > 1) {
      // Cannot reuse links for multiple items
      return this.showCreateLinkModalFor(bundles, directories, permission, root)
    }

    // eslint-disable-next-line import/no-cycle
    const { SharingModalComponent } = await import('../components/sharingModal.component')

    const m = this.ngbModal.open(SharingModalComponent, { size: 'transparent' })
    m.componentInstance.bundles = bundles.slice()
    m.componentInstance.directories = directories.slice()
    m.componentInstance.permission = permission
    m.componentInstance.root = root
    await m.result
  }

  showCreateLinkModalFor (
    bundles: MediaFileBundle[],
    directories: MediaFile[],
    permission: MediaRootPermission,
    root: MediaRootDetail,
  ): Promise<void> {
    const modal = this.ngbModal.open(SharingCreateLinkModalComponent)
    modal.componentInstance.bundles = bundles.slice()
    modal.componentInstance.directories = directories.slice()
    modal.componentInstance.permission = permission
    modal.componentInstance.root = root
    return modal.result.catch(() => null)
  }

  getTitleForToken (token: OneTimeAccessToken, options?: { includeName: boolean }): string {
    const includeName = options?.includeName
    const count = Math.max(0, token.mediaRootPermissions.length - 1)
    const item = token.sharedBundles.concat(token.sharedDirectories)[0]?.name || token.url
    const recipient = token.user.displayName ?? this.translate.instant('Unknown')
    let title = ''

    if (token.isEasySharingForBundle || token.isEasySharingForDirectory) {
      if (includeName) {
        title = this.translate.instant('Anonymous read-only link to { item }', { item })
      } else {
        title = this.translate.instant('Anonymous read-only link')
      }
    } else if (token.user.isExternal && !token.user.email) {
      if (includeName) {
        // Anonymous with filename
        title = this.translate.instant(
          `Anonymous link {count, plural,
            =0{to {item}}
            one{to {item} and one other item}
            other{to {item} and {count} other items}}`,
          { recipient, item, count })
      } else {
        // Anonymous without filename
        title = this.translate.instant(
          `Anonymous link {count, plural,
            =0{}
            one{to this and one other item}
            other{to this and {count} other items}}`,
          { recipient, count })
      }
    } else {
      if (includeName) {
        // For a user, with filename
        title = this.translate.instant(
          `Link for { recipient } {count, plural,
            =0{to {item}}
            one{to {item} and one other item}
            other{to {item} and {count} other items}}`,
          { recipient, item, count })
      } else {
        // For a user, without filename
        title = this.translate.instant(
          `Link for { recipient } {count, plural,
            =0{}
            one{to this and one other item}
            other{to this and {count} other items}}`,
          { recipient, count })
      }
    }
    return title
  }

  async addToShowroom (bundles: MediaFileBundle[]): Promise<void> {
    const showroomsPromise = this.api.mediaLibrary.getAllMediaFiles({
      isShowroom: true,
      present: true,
    } as any)

    const showroom = await this.picker.pickObject({
      items: showroomsPromise.then(items => items.map(item => ({
        object: item,
        name: item.name,
        group: this.translate.instant('Showrooms'),
        iconClass: 'far fa-folder text-rare',
      }))),
    }).catch(() => null)

    if (!showroom) {
      return
    }

    try {
      const files = await this.api.mediaLibrary.getAllMediaFilesForBundles({
        allMediaFilesForBundlesRequest: {
          bundles: bundles.map(x => x.id),
        },
      })

      const input = files.map(x => x.fullPath)
      if (!showroom.fullPath) {
        throw Error('Invalid showroom')
      }
      await this.api.storage.copyFiles({
        fileCopyEndpointRequest: {
          input,
          destination: showroom.fullPath,
          hardlink: true,
          sync: true,
        },
      })

      this.notify.success(this.translate.instant('Added to showroom'))
    } catch (response) {
      this.notify.errorFromResponse(this.translate.instant('Error'), response)
    }
  }

  async sendToHomeWorkspace (options: { paths: string[], bundles: MediaFileBundle[] }): Promise<void> {
    const usersPromise = this.api.main.getAllUsers({})

    const user = await this.picker.pickObject({
      items: usersPromise.then(items => items
        .filter(x => !!x.home)
        .sort(
          firstBy<ElementsUser, boolean>(u => u.id !== this.identity.user!.id)
            .thenBy(u => u.displayName),
        )
        .map(item => ({
          object: item,
          name: item.displayName,
          group: this.translate.instant('Users'),
          iconClass: 'far fa-user',
        })),
      ),
    }).catch(() => null)

    if (!user) {
      return
    }

    await this.api.storage.shareToHomeWorkspace({
      shareToHomeWorkspaceEndpointRequest: {
        paths: options.paths,
        bundles: options.bundles.map(x => x.id),
        user: user.id,
        name: new Date().toLocaleString(),
      },
    })

    this.notify.success(this.translate.instant('Sent'))
  }
}
